/**
 * Iter XX.274 — Gully Pro Founding 100 · landing page.
 *
 * Public / anonymous-safe. Web-only. Renders under two gates:
 *   · backend `founding100_enabled` feature flag ON, AND
 *   · verified paid count < 100.
 *
 * When either gate blocks exposure, the backend `/api/founding100/status`
 * returns `{ open: false, reason: "hidden" | "full" }` and this page
 * renders either a soft "coming soon" state (hidden) or the explicit
 * "Founding 100 is full." state.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { trackFunnel } from "../lib/activationFunnel";
import PageErrorBoundary from "../components/PageErrorBoundary";
import { Trophy, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Founding100Landing() {
  return (
    <PageErrorBoundary pageName="Founding100">
      <Inner />
    </PageErrorBoundary>
  );
}

function Inner() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/founding100/status");
        setState(data || { open: false });
      } catch {
        setState({ open: false });
      }
      try { trackFunnel("founding100_page_view", { surface: "landing" }); }
      catch { /* noop */ }
    })();
  }, []);

  const onCheckout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      trackFunnel("founding100_checkout_started", {});
    } catch { /* noop */ }
    try {
      const { data } = await api.post("/founding100/checkout", {
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setBusy(false);
      toast.error("Couldn't start checkout — try again.");
    } catch (e) {
      setBusy(false);
      const reason = e?.response?.data?.detail;
      if (reason === "full_or_hidden") {
        toast.error("Founding 100 is full.");
        setState((s) => ({ ...(s || {}), open: false, reason: "full" }));
      } else {
        toast.error("Couldn't start checkout — try again.");
      }
    }
  };

  if (!state) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-200 p-6">
      Loading…
    </div>;
  }

  const full = state.reason === "full";
  const hidden = state.reason === "hidden";

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-2xl mx-auto px-6 pt-16 pb-24">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-700/50 flex items-center justify-center">
            <Trophy size={16} className="text-emerald-300" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400/90 font-semibold" data-testid="founding100-eyebrow">
            Gully Pro · Founding 100
          </div>
        </div>
        <h1 className="text-3xl sm:text-[2.5rem] font-semibold leading-tight text-neutral-50 mb-4">
          Don’t just use the next version of Gully. Help build it.
        </h1>
        <p className="text-base text-neutral-300 leading-relaxed mb-6">
          We’re building the next version of Gully for serious cricketers —
          live practice, deeper video analysis, season-long development,
          and a Gully that gets to know your game over time.
        </p>
        <p className="text-base text-neutral-300 leading-relaxed mb-10">
          I’m opening 100 founding spots for cricketers who want to help
          shape Gully Pro.
        </p>

        <div className="rounded-2xl border border-emerald-800/60 bg-emerald-950/30 p-6 mb-8" data-testid="founding100-offer-card">
          <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold mb-2">
            Founding membership
          </div>
          <div className="flex items-baseline gap-2 mb-4">
            <div className="text-3xl font-semibold text-neutral-50">US$99</div>
            <div className="text-sm text-neutral-400">one-time</div>
          </div>
          <ul className="text-sm text-neutral-300 leading-relaxed list-disc pl-5 space-y-1 mb-4">
            <li>12 months of Gully Plus</li>
            <li>Early access to the Gully Pro beta</li>
            <li>Priority access to new Pro features as they launch</li>
            <li>Founding-member pricing when Gully Pro launches</li>
            <li>Access to a private founding-member feedback group</li>
            <li>Priority access to future founder-led video review opportunities / clinics where offered</li>
          </ul>
          <div className="rounded-lg bg-amber-950/40 border border-amber-800/60 px-3 py-2 mb-4" data-testid="founding100-disclosure">
            <div className="text-[11px] text-amber-200 leading-relaxed">
              Gully Pro is still being built. Some Pro features are not
              available yet, and no exact launch date is promised.
            </div>
          </div>
          {full ? (
            <div className="text-sm text-neutral-300" data-testid="founding100-full-state">
              Founding 100 is full.
            </div>
          ) : hidden ? (
            <div className="text-sm text-neutral-500" data-testid="founding100-hidden-state">
              Founding 100 isn’t open right now. Check back soon.
            </div>
          ) : (
            <>
              {/* Iter XX.275 — coaching frame anchor. No universal
                  savings claim; positions Plus as a between-sessions
                  companion to the coaching, practices and matches the
                  cricketer already invests in. */}
              <p
                data-testid="founding100-coaching-anchor"
                className="text-[12px] text-neutral-400 leading-relaxed mb-4"
              >
                US$99 once gives you a full year of Gully Plus + early
                Gully Pro access — built to support your cricket
                between the coaching sessions, practices and matches
                you already have.
              </p>
              <button
                onClick={onCheckout}
                disabled={busy}
                data-testid="founding100-cta"
                className={
                  "w-full inline-flex items-center justify-center gap-2 "
                  + "px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 "
                  + "active:bg-emerald-600 transition-colors text-neutral-950 "
                  + "font-medium disabled:opacity-50"
                }
              >
                Become a Founding Member
              </button>
              {typeof state.remaining === "number" && (
                <div className="mt-3 text-xs text-neutral-500 text-center" data-testid="founding100-remaining">
                  {state.remaining === 100
                    ? "100 founding spots"
                    : `${state.remaining} of ${state.cap || 100} spots remaining`}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
